import { type LoadOptions, RudderAnalytics } from '@rudderstack/analytics-js'
import useAuthStore from '~/stores/auth'
import debounce from 'lodash/debounce'
import type {
  RSDownloadEvent,
  RSFilterType,
  RSFormEvent,
  RSVideoEvent,
} from '~/types/rudderstack.type'
import { IMPERSONATING, USER_INFO } from '~/src/constants/common'
import type { TSubscriptionResponse } from '~/src/utils/subscriptions'
import type { TGeneral } from '~/src/types/common'
import { getCookieConsentByCategory } from '~/src/utils/cookie-script'
import { COOKIE_SCRIPT_CATEGORIES } from '~/src/enums/cookie-script.enum'

export default defineNuxtPlugin({
  name: 'rudderstack',
  dependsOn: ['cookies'],
  setup(nuxtApp) {
    const authStore = useAuthStore()
    const membershipStore = useMembershipsStore()
    const { $config, $cookies, $restClient }: TGeneral = nuxtApp

    const DATA_PLANE_URL = $config.public.rudderstack.dataPlaneUrl
    const WRITE_KEY = $config.public.rudderstack.writeKey
    const OPTIONS = {
      logLevel: 'DEBUG',
      onLoaded: async () => {
        if (authStore.authenticated) {
          await identifyUser()
        }
      },
    } satisfies LoadOptions

    const rudderAnalytics = new RudderAnalytics()

    const performanceCookiesAccepted = getCookieConsentByCategory({
      category: COOKIE_SCRIPT_CATEGORIES.PERFORMANCE,
      onChange: (accepted) => {
        accepted
          ? rudderAnalytics.load(WRITE_KEY, DATA_PLANE_URL, OPTIONS)
          : rudderAnalytics.reset()
      },
    })

    const setCurrentSubscriptions = async () => {
      if (membershipStore.hasRetrievedSubscriptions) {
        return membershipStore.currentSubscriptions
      }

      const subscriptions = (await $restClient.getSubscriptions(
        false,
      )) as TSubscriptionResponse
      membershipStore.setupCurrentSubscriptions(subscriptions)
    }

    const updateCookieData = () => {
      if (!authStore.user) return

      const { email, company } = authStore.user
      const userInfo = $cookies.get(USER_INFO)
      const updatedUserInfo = {
        ...userInfo,
        email,
        company,
      }
      $cookies.set(USER_INFO, updatedUserInfo)
    }

    /**
     * Update the current authenticated user data in the store
     * with the email and company latest information
     */
    const setAuthenticatedUserData = async () => {
      const cookieInfo = $cookies.get(USER_INFO)
      // No need to go to the backend if there is already a company and email.
      if (!cookieInfo || (cookieInfo.company && cookieInfo.email)) return
      if (authStore.queriedUserData) return

      // Access graphql client dynamically to avoid circular dependency
      const $graphqlClient = (nuxtApp as any).$graphqlClient
      if ($graphqlClient) {
        const userInfo = await $graphqlClient.getAuthenticatedUserData()
        authStore.setPartialUserInfo(userInfo)
        updateCookieData()
      }
    }

    const getUserData = async () => {
      if (!authStore.authenticated || !authStore.user) return {}

      await setCurrentSubscriptions()
      await setAuthenticatedUserData()
      const { databaseId, name, firstName, lastName, email, company } =
        authStore.user || {}
      return {
        email_encrypted: btoa(email),
        wordpress_id: databaseId,
        email,
        name,
        firstName,
        lastName,
        company,
        subscriptions: membershipStore.currentSubscriptions,
      }
    }

    const getBaseParams = () => {
      const params: Record<string, boolean> = {}

      if ($cookies.get(IMPERSONATING) ?? false) {
        params.impersonating = true
      }

      return {
        page_title: window.document.title,
        page_url: useRoute().path,
        uri:
          window.location.pathname +
          window.location.search +
          window.location.hash,
        submission_time: new Date(),
        platform: 'www',
        ...params,
      }
    }

    /**
     * Track user log in
     */
    const trackLogIn = async () => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('logged_in', data)

      await identifyUser()
    }

    /**
     * Track user log out
     */
    const trackLogOut = async () => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('logged_out', {
        ...data,
        ...getBaseParams(),
      })
    }

    /**
     * Track page views. We debounce the event to avoid sending too many events
     * to RudderStack. Specifically, in the pages with tabs, where the event
     * was triggered twice.
     */
    const trackPageView = debounce(async () => {
      if (!performanceCookiesAccepted.value) return
      const data = {
        ...getBaseParams(),
        ...(await getUserData()),
      }

      rudderAnalytics.track('page_viewed', data)
    }, 1000)

    /**
     * Track form submissions
     */
    const trackFormSubmissions = async (formData: RSFormEvent) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()

      rudderAnalytics.track('form_submitted', {
        ...data,
        ...formData,
        ...getBaseParams(),
      })
    }

    /**
     * Track form start event in a form
     */
    const trackFormStart = async (formData: RSFormEvent) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()

      rudderAnalytics.track('form_start', {
        ...data,
        ...formData,
        ...getBaseParams(),
      })
    }

    /**
     * Track form field changes in a form
     */
    const trackFormFieldChange = debounce(
      async (formFieldData: {
        form_id: string | number
        form_name: string
        form_type: string
        field_name: string
        field_type: string
        field_value?: string | any
      }) => {
        if (!performanceCookiesAccepted.value) return
        const data = await getUserData()
        rudderAnalytics.track('field_changed', {
          ...formFieldData,
          ...data,
          ...getBaseParams(),
        })
      },
      500,
    )

    /**
     * Track video ended
     */
    const trackVideoEnded = async (videoData: RSVideoEvent) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('video_completed', {
        ...data,
        ...videoData,
        ...getBaseParams(),
      })
    }

    /**
     * Track video started
     */
    const trackVideoStarted = async (videoData: RSVideoEvent) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('video_started', {
        ...data,
        ...videoData,
        ...getBaseParams(),
      })
    }

    /**
     * Track video progress. Debounced to avoid sending too many events
     * to RudderStack.
     *
     * The `progress` event from the vimeo player is not always consistent with the
     * time it takes to fire the event. That's why we debounce the event to 7 seconds
     * to avoid sending events when the user is still watching the video.
     */
    const trackVideoProgress = debounce(async (videoData: RSVideoEvent) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('video_progress_made', {
        ...data,
        ...videoData,
        ...getBaseParams(),
      })
    }, 7000)

    /**
     * Track error shown on the website
     */
    const trackErrorShown = async (errorData: {
      error_code: string | number
      error_message: string
      error_type: string
    }) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('error_shown', {
        ...data,
        ...errorData,
        ...getBaseParams(),
      })
    }

    /**
     * Track downloads on the website. That includes report downloads and chart downloads
     */
    const trackDownloads = async (reportData: RSDownloadEvent) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('file_downloaded', {
        ...data,
        ...getBaseParams(),
        ...reportData,
        file_type: reportData.file_type || 'pdf',
      })
    }

    /**
     * Track chart interactions on the website.
     * We debounced this function because
     * it's trigger on hover state over the chart, and that event is triggered many times
     * in a short time.
     */
    const trackChartInteraction = debounce(
      async (chartData: {
        chart_title: string
        chart_type: string
        start_date?: string
        end_date?: string
        current_filters?: object
      }) => {
        if (!performanceCookiesAccepted.value) return
        const data = await getUserData()

        rudderAnalytics.track('chart_interaction', {
          ...data,
          ...getBaseParams(),
          ...chartData,
        } as any)
      },
      2000,
    )

    const trackFilterApplied = debounce(
      async (eventData: {
        filter_values: object
        filter_type: RSFilterType
        chart_title: string
      }) => {
        if (!performanceCookiesAccepted.value) return
        const data = await getUserData()

        rudderAnalytics.track('filter_applied', {
          ...data,
          ...getBaseParams(),
          ...eventData,
        } as any)
      },
      1000,
    )

    /**
     * Track chart interactions on the website
     */
    const trackChartDownload = async (
      chartData: {
        chart_title?: string
        chart_type?: string
        element_id?: string
        download_format?: 'png' | 'xlsx'
      } = { chart_type: 'lines' },
    ) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.track('chart_download', {
        ...data,
        ...chartData,
        ...getBaseParams(),
      } as any)
    }

    /**
     * Track content share in the website
     */
    const trackContentShare = async (shareData: {
      share_method: 'twitter' | 'linkedin' | 'link'
      content: object
    }) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()

      rudderAnalytics.track('content_share', {
        ...data,
        ...shareData,
        ...getBaseParams(),
        content_type: 'index',
      } as any)
    }

    /**
     * Track search done on the website
     */
    const trackSearchDone = async (searchData: {
      search_query: string
      search_results_count: number
      search_type: string
    }) => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()

      rudderAnalytics.track('search_done', {
        ...data,
        ...searchData,
        ...getBaseParams(),
      })
    }

    /**
     * Track newsletter subscription
     */
    const trackNewsletterSubscription = async (newsletterData: {
      newsletter_id: string | number
      newsletter_name: string
    }) => {
      if (!performanceCookiesAccepted.value) return
      rudderAnalytics.track('newsletter_subscribed', {
        ...(await getUserData()),
        ...newsletterData,
        ...getBaseParams(),
      })
    }

    const trackContentGatedInteraction = debounce(async () => {
      if (!performanceCookiesAccepted.value) return
      rudderAnalytics.track('content_gated_interaction', {
        ...(await getUserData()),
        ...getBaseParams(),
      })
    }, 500)

    /**
     * Identify the user in RudderStack
     */
    const identifyUser = async () => {
      if (!performanceCookiesAccepted.value) return
      const data = await getUserData()
      rudderAnalytics.identify(data.email_encrypted as any, data)
      rudderAnalytics.track('identified', { ...data, ...getBaseParams() })
    }

    /**
     * Hook to track page views
     */
    nuxtApp.hook('page:finish', async () => {
      await trackPageView()
    })

    return {
      provide: {
        rudderstack: {
          trackPageView,
          trackLogIn,
          trackLogOut,
          trackFormSubmissions,
          trackFormStart,
          trackFormFieldChange,
          trackVideoEnded,
          trackVideoStarted,
          trackVideoProgress,
          trackDownloads,
          trackChartDownload,
          trackChartInteraction,
          trackFilterApplied,
          trackContentShare,
          trackSearchDone,
          trackNewsletterSubscription,
          trackContentGatedInteraction,
          trackErrorShown,
        },
      },
    }
  },
})
